import { Injectable } from '@angular/core';
import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, concatMap, map, takeUntil } from 'rxjs/operators';

import * as uploadActions from '../upload/upload.actions';
import { UploadKeys } from 'src/app/models/upload';
import { StudyService } from '../../services/study/study.service';
import { ModuleService } from '../../services/module/module.service';
import { ModuleItemService } from '../../services/module-item/module-item.service';
import { InterventionService } from '../../services/intervention/intervention.service';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { InformativePagesService } from '../../services/informative-pages/informative-pages.service';
import { TemplatesService } from '../../services/templates/templates.service';

function getActionFromHttpEvent(uuid: string, event: HttpEvent<any>) {
  switch (event.type) {
    case HttpEventType.Sent: {
      return uploadActions.uploadStarted({ uuid });
    }
    case HttpEventType.UploadProgress: {
      return uploadActions.uploadProgress({
        uuid,
        progress: Math.round((100 * event.loaded) / event.total),
      });
    }
    case HttpEventType.ResponseHeader:
    case HttpEventType.Response: {
      if (event.status === 200) {
        return uploadActions.uploadCompleted({ uuid, uploaded: event instanceof HttpResponse ? event.body : null });
      } else {
        return uploadActions.uploadError({
          uuid,
          error: event.statusText,
        });
      }
    }
    default: {
      return uploadActions.uploadError({
        uuid,
        error: `Unknown Event: ${JSON.stringify(event)}`,
      });
    }
  }
}

@Injectable()
export class UploadEffects {
  uploadRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadActions.uploadRequest),
      concatMap(({ uuid, file, keyForUpload, idsForUpload }) => {
        switch (keyForUpload) {
          case UploadKeys.UPLOAD_FROM_STUDY: {
            return this.studyService.uploadStudyImage(file, idsForUpload).pipe(
              takeUntil(this.actions$.pipe(ofType(uploadActions.uploadCancel))),
              map((event) => getActionFromHttpEvent(uuid, event)),
              catchError((error) => of(uploadActions.uploadError({ uuid, error })))
            );
          }
          case UploadKeys.UPLOAD_FROM_INTERVENTION: {
            return this.interventionService.uploadInterventionImage(file, idsForUpload).pipe(
              takeUntil(this.actions$.pipe(ofType(uploadActions.uploadCancel))),
              map((event) => getActionFromHttpEvent(uuid, event)),
              catchError((error) => of(uploadActions.uploadError({ uuid, error })))
            );
          }
          case UploadKeys.UPLOAD_FROM_SUBSCRIPTION: {
            return this.subscriptionService.uploadSubscriptionInformativePageImage(file, idsForUpload).pipe(
              takeUntil(this.actions$.pipe(ofType(uploadActions.uploadCancel))),
              map((event) => getActionFromHttpEvent(uuid, event)),
              catchError((error) => of(uploadActions.uploadError({ uuid, error })))
            );
          }
          case UploadKeys.UPLOAD_FROM_MODULE: {
            return this.moduleService.uploadModuleImage(file, idsForUpload).pipe(
              takeUntil(this.actions$.pipe(ofType(uploadActions.uploadCancel))),
              map((event) => getActionFromHttpEvent(uuid, event)),
              catchError((error) => of(uploadActions.uploadError({ uuid, error })))
            );
          }
          case UploadKeys.UPLOAD_FROM_MODULE_ITEM: {
            return this.moduleItemService.uploadModuleItemFile(file, idsForUpload).pipe(
              takeUntil(this.actions$.pipe(ofType(uploadActions.uploadCancel))),
              map((event) => getActionFromHttpEvent(uuid, event)),
              catchError((error) => of(uploadActions.uploadError({ uuid, error })))
            );
          }
          case UploadKeys.UPLOAD_FROM_TEMPLATE: {
            return this.templateService.uploadTemplateLogo(file, idsForUpload).pipe(
              takeUntil(this.actions$.pipe(ofType(uploadActions.uploadCancel))),
              map((event) => getActionFromHttpEvent(uuid, event)),
              catchError((error) => of(uploadActions.uploadError({ uuid, error })))
            );
          }
          default:
            return of(uploadActions.noopAction());
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private studyService: StudyService,
    private interventionService: InterventionService,
    private subscriptionService: SubscriptionService,
    private informativePagesService: InformativePagesService,
    private moduleService: ModuleService,
    private moduleItemService: ModuleItemService,
    private templateService: TemplatesService
  ) {}
}
